// URL hash encoding — serialise / deserialise a PatternManifest as a
// short base64url-safe string for use in /api/pattern/[hash].png paths.
//
// Two-part encoding:
//   1. JSON.stringify(manifest) → UTF-8 bytes → base64url (the actual
//      manifest payload).
//   2. Append `:<theme>` so light + dark are independently cacheable.
//
// The full hash string is `<base64url-payload>:<theme>`. Vercel CDN keys
// by full URL so the theme suffix flips the cache automatically.
//
// Manifests are small (~120-220 bytes JSON) so the resulting URL stays
// well under any reasonable URL-length limit.

import { parsePatternManifest } from './parse';
import type { PatternManifest } from './types';

export type ManifestThemeName = 'light' | 'dark';

const THEME_VALUES: readonly ManifestThemeName[] = ['light', 'dark'];

// ALLOW-UNKNOWN: runtime type-guard for arbitrary URL-decoded input
function isThemeName(v: unknown): v is ManifestThemeName {
  return typeof v === 'string' && (THEME_VALUES as readonly string[]).includes(v);
}

function utf8Encode(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

function utf8Decode(bytes: Uint8Array): string {
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

function base64UrlEncode(bytes: Uint8Array): string {
  // Browser + Node both have `btoa` via global, but `btoa` only handles
  // Latin-1. Use buffer-style encoding via String.fromCharCode chunked
  // so we stay platform-agnostic.
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  // `btoa` exists in browsers and modern Node (>=16).
  const b64 =
    typeof btoa === 'function' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(s: string): Uint8Array {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const b64 = padded + padding;
  if (typeof atob === 'function') {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  return new Uint8Array(Buffer.from(b64, 'base64'));
}

/**
 * Encode a manifest + theme as the URL hash segment used in
 * `/api/pattern/<hash>.png`. Same input always produces the same string,
 * so CDN cache keys are stable across renders / sessions / devices.
 */
export function encodeManifestHash(manifest: PatternManifest, theme: ManifestThemeName): string {
  const json = JSON.stringify(manifest);
  const bytes = utf8Encode(json);
  return `${base64UrlEncode(bytes)}.${theme}`;
}

export interface DecodedManifestHash {
  readonly manifest: PatternManifest;
  readonly theme: ManifestThemeName;
}

/**
 * Decode the URL hash segment back into a validated manifest + theme.
 * Returns `null` for any invalid input — corrupted base64, JSON parse
 * failure, manifest schema mismatch, or unknown theme.
 *
 * Used at the boundary in `/api/pattern/[hash]/route.ts`.
 */
export function decodeManifestHash(hash: string): DecodedManifestHash | null {
  if (typeof hash !== 'string' || hash.length === 0) return null;

  // Strip any trailing extension like `.png` (Next.js may pass it through).
  const stripped = hash.replace(/\.png$/, '');

  const lastDot = stripped.lastIndexOf('.');
  if (lastDot < 1) return null;

  const payload = stripped.slice(0, lastDot);
  const themeRaw = stripped.slice(lastDot + 1);
  if (!isThemeName(themeRaw)) return null;

  let json: string;
  try {
    json = utf8Decode(base64UrlDecode(payload));
  } catch {
    // SILENT-CATCH: malformed base64 / utf-8 → caller treats null return as "invalid hash"
    return null;
  }

  // ALLOW-UNKNOWN: JSON.parse return is intentionally unknown until parsePatternManifest validates
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    // SILENT-CATCH: malformed JSON → caller treats null return as "invalid hash"
    return null;
  }

  const manifest = parsePatternManifest(parsed);
  if (manifest === null) return null;

  return { manifest, theme: themeRaw };
}
